import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { countriesQuery, type Country } from '@/data';
import {
  ColorBar,
  DEFAULT_WEIGHTS,
  DIMENSIONS,
  scoreCountry,
  type CountryMatchResult,
  type Dimension,
  type ScoringWeights,
} from '@/features/quiz';
import { cn } from '@/components/ui/cn';
import { TASTE_DIMENSIONS, type TasteDimension } from '../presets';
import { uiLang } from '@/i18n/lang';

/**
 * Returns a weights vector that boosts the selected dimension and damps the
 * rest. Bypasses deriveWeights since we have no Answers to map from.
 */
function singleDimWeights(focus: Dimension): ScoringWeights {
  const out = { ...DEFAULT_WEIGHTS };
  for (const d of DIMENSIONS) {
    out[d] = d === focus ? 3 : 0.3;
  }
  return out;
}

type DimRanking = { match: CountryMatchResult; country: Country }[];

function rankCountriesByDim(
  countries: Country[],
  focus: TasteDimension,
): DimRanking {
  const weights = singleDimWeights(focus);
  const matches: DimRanking = [];
  for (const c of countries) {
    if (!c.metrics) continue;
    const m = scoreCountry(c, {}, weights);
    if (m.excluded) continue;
    matches.push({ match: m, country: c });
  }
  matches.sort((a, b) => b.match.score - a.match.score);
  return matches.slice(0, 3);
}

/**
 * Precompute top-3 for all 6 taste dimensions in one pass. Chip clicks then
 * become O(1) lookups instead of re-scoring 30 countries each time.
 */
function computeAllDimRankings(
  countries: Country[],
): Record<TasteDimension, DimRanking> {
  const out = {} as Record<TasteDimension, DimRanking>;
  for (const d of TASTE_DIMENSIONS) {
    out[d] = rankCountriesByDim(countries, d);
  }
  return out;
}

export function DimensionMicroPreview() {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n.language);
  const { data: file } = useQuery(countriesQuery());
  const [focus, setFocus] = useState<TasteDimension | null>(null);

  const weights = useMemo(
    () => (focus ? singleDimWeights(focus) : DEFAULT_WEIGHTS),
    [focus],
  );

  // Precompute every dimension's top-3 once per countries load.
  const allRankings = useMemo(
    () => (file ? computeAllDimRankings(file.countries) : null),
    [file],
  );
  const top3 = focus && allRankings ? allRankings[focus] : [];

  return (
    <section
      id="taste"
      className="border-y border-ink-100 bg-paper-2/40"
      aria-labelledby="taste-title"
    >
      <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
        <h2
          id="taste-title"
          className="font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl"
        >
          {t('home.taste.title')}
        </h2>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink-500 sm:text-lg">
          {t('home.taste.sub')}
        </p>

        <p className="mt-8 font-mono text-[11px] uppercase tracking-wider text-ink-500">
          {t('home.taste.question')}
        </p>
        <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label={t('home.taste.question')}>
          {TASTE_DIMENSIONS.map((d) => {
            const selected = focus === d;
            return (
              <button
                key={d}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setFocus(d)}
                data-testid={`taste-chip-${d}`}
                className={cn(
                  'rounded-full border px-4 py-1.5 font-mono text-xs uppercase tracking-wider transition-colors',
                  selected
                    ? 'border-terra-600 bg-terra-600 text-paper'
                    : 'border-ink-100 bg-paper text-ink-700 hover:border-ink-300 hover:bg-paper-2',
                )}
              >
                {t(`home.taste.dim.${d}`)}
              </button>
            );
          })}
        </div>

        <div className="mt-8 rounded-xl border border-ink-100 bg-paper px-5 py-6 sm:px-8 sm:py-8">
          {!focus ? (
            <p className="font-mono text-sm text-ink-400">
              {t('home.taste.empty')}
            </p>
          ) : (
            <>
              <p className="font-mono text-[11px] uppercase tracking-wider text-ink-500">
                {t('home.taste.basedOn', { dim: t(`home.taste.dim.${focus}`) })}
              </p>
              <ul className="mt-4 space-y-4" data-testid="taste-top3">
                {top3.map(({ match, country }, i) => {
                  const name = lang === 'ru' ? country.name_ru : country.name_en;
                  const percent = Math.round(match.score * 100);
                  return (
                    <li
                      key={country.code}
                      className="flex items-center gap-4"
                      data-testid={`taste-row-${i + 1}`}
                    >
                      <span className="w-4 font-mono text-xs tabular-nums text-ink-400">
                        {i + 1}
                      </span>
                      <span className="text-xl leading-none" aria-hidden="true">
                        {country.flag_emoji}
                      </span>
                      <span className="w-40 shrink-0 font-display text-lg font-medium italic text-ink-900">
                        {name}
                      </span>
                      <span className="hidden flex-1 sm:block">
                        <ColorBar
                          components={match.components}
                          weights={weights}
                          t={t}
                          size="sm"
                        />
                      </span>
                      <span className="ml-auto shrink-0 font-mono text-sm font-semibold tabular-nums text-terra-600">
                        {percent}%
                      </span>
                    </li>
                  );
                })}
              </ul>
              <Link
                to="/quiz"
                className="mt-6 inline-flex font-mono text-xs font-medium uppercase tracking-wider text-terra-600 hover:underline"
                data-testid="taste-take-full"
              >
                {t('home.taste.takeFull')}
              </Link>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
